import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LogOut, Settings, User } from 'lucide-react';
import { useAuth } from '@/hooks/useAuthApi';
import { ThemeToggle } from '@/components/ThemeToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface TopNavigationProps {
  className?: string;
}

export const TopNavigation: React.FC<TopNavigationProps> = ({ className = '' }) => {
  const { userData, logout } = useAuth();
  const navigate = useNavigate();

  const getUserInitials = () => {
    if (!userData) return 'U';
    return userData.displayName?.charAt(0).toUpperCase() ||
           userData.email?.charAt(0).toUpperCase() || 'U';
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <ThemeToggle />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Avatar className="h-[30px] w-[30px] cursor-pointer rounded-lg border border-border bg-secondary transition-all hover:ring-2 hover:ring-ring/20">
            <AvatarImage src={userData?.photoURL || undefined} alt={userData?.displayName || 'User'} />
            <AvatarFallback className="bg-secondary text-secondary-foreground text-xs">
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <div className="px-2 py-1.5">
            <p className="text-sm font-medium text-foreground truncate">
              {userData?.displayName || 'User'}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {userData?.email}
            </p>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/account-settings')}>
            <User className="mr-2 h-4 w-4" />
            Account settings
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/organization-settings')}>
            <Settings className="mr-2 h-4 w-4" />
            Organization settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
